// ════════════════════════════════════════════════════════════════════════════
// From resolved links to the attached result BLOCKS — one per pillar.
//
// The write seam owns the store and therefore owns resolution; this module
// owns everything that follows from it and nothing that precedes it, so the
// rules below are unit-testable without a store, a session or a tool.
//
// Three rules decide a block (decision 8, ruled 2026-08-22, and the pillar
// merge, ruled 2026-08-25, retiring the one-search-per-block refusal):
//
//   RESOLUTION IS NEVER REFUSED. A link that resolves in the writer's live
//   store contributes its rows to a PILLAR block; a citation link or ordinary
//   URL that does not resolve makes the block materialise as the PLAIN LINK
//   kind — pasted as a link, unaugmented, never an error. Ownership died with
//   the ruling: a teammate's link and a raw web URL both land, as links.
//
//   ONE BLOCK PER PILLAR, HOWEVER MANY SEARCHES. A fully-resolving block's
//   links partition by the PILLAR of the search each resolved to — never by
//   the search itself — and every pillar the links span materialises as its
//   own consecutive block, ordered by the pillar's first appearance among the
//   authored links. All of one pillar's searches merge into ONE block whose
//   items are the union of their rows (authored-link order, then row order),
//   deduped by the ruled item identity below, the capture caption stating the
//   contributing searches' WINDOW. The 2026-08-25 ruling replaced the
//   composition refusal this used to be with exactly this split-and-merge.
//
//   AN ACTION TOKEN THAT RESOLVES NOWHERE STAYS AN ADDRESS — and an address
//   never becomes a link (U9: `/r/` is not a destination; it prints dead for
//   every later reader). Such a block is handed back unmaterialised, and the
//   research-address guard that runs over every write refuses it with the
//   ruled leak-guard copy — the ONE remaining refusal, in its one ratified
//   home. Detection uses `offeredLinkActionAddress`, the link-position half of
//   the guard's own reading (one grammar, one home — phase-4 validation,
//   required fix 2: it covers every dress, the naked token and the rootless
//   `/r/` path included), so "left for the guard" and "the guard fires" are
//   the same predicate by construction.
//
// THE ITEM IDENTITY (decision 5, ratified 2026-08-25). Two rows are the same
// item exactly when their row kinds match AND a canonical identity matches,
// in precedence: shared native identifiers — equal values on every key both
// rows carry, at least one key shared — else the normalized captured
// destination URL. A pair neither identity can judge is NEVER merged: no
// identity, no merge, both occurrences render. On a duplicate, the occurrence
// with a captured detail wins (the ruled "its more detailed version"); with
// both or neither detailed, the newer capture wins; the kept item occupies
// the FIRST occurrence's slot, and it keeps ONLY its own addresses and label
// — nothing is transplanted from a discarded occurrence, whose label dies
// with it (decision 4). The identity is proven PAIRWISE and carries no
// transitivity of its own, so a row that proves two rows already held apart
// are each the same item as it folds them into one — how many proven
// duplicates survive is a fact about the rows, never about the order the
// writer pasted their links in.
// ════════════════════════════════════════════════════════════════════════════

import { offeredLinkActionAddress } from '../session/orchestration/tool-protocol/follow-up-addresses.js';
import type { PillarCode } from '../session/orchestration/tool-protocol/follow-up-refs.js';
import type {
  RowRefRecord,
  SearchRefRecord,
} from '../session/orchestration/tool-protocol/types.js';
import {
  ATTACH_FIELD_SEPARATOR,
  // The PERSISTED block's line ceiling, aliased because the barrel re-exports
  // the AUTHORING block's under the same name (see `index.ts`) and this bound
  // is about the line we write, not the one a model writes.
  ATTACH_MAX_LINE_LENGTH as ATTACH_PERSISTED_MAX_LINE_LENGTH,
  AttachDirectiveKey,
  type CapturedAtRange,
} from './grammar.js';
import type { AttachLinkItem, ParsedAttachBlock } from './parse-attach-block.js';

/** One row a link contributed. */
export interface ResolvedAttachRow {
  readonly record: RowRefRecord;
  /** The citation link the writer attached this row under, or null when the
   *  row arrived through the SEARCH's action link — a search names its rows
   *  collectively and no per-row link was pasted. */
  readonly url: string | null;
  /** The markdown label the writer gave a ONE-ROW link (U14), or null — for a
   *  bare URL, and for every row a WHOLE-SEARCH link contributed: that link's
   *  label is block framing (`ResolvedAttachLink.searchLabel`), never a
   *  per-row fact (ruled 2026-08-25, decision 2). It is the writer's own name
   *  for the row and the only title the generic `Links` kind has. */
  readonly label: string | null;
  /** What the drill that opened this row answered with, or null when nothing
   *  has opened it. The ruled smart-detail behaviour (2026-08-19) reads it: a
   *  full search attaches its rows lean, except the ones already drilled,
   *  which attach in their DETAIL shape — one block, mixed shapes. */
  readonly detailPayload: unknown;
}

/** What one attached link resolved to in the writer's live store. */
export interface ResolvedAttachLink {
  readonly search: SearchRefRecord;
  /** ISO-8601 capture time of that search (U16). */
  readonly capturedAt: string;
  /** The writer's label on a link that named the WHOLE search, or null — for
   *  an unlabelled link and for every one-row link, whose label rides its row
   *  instead (`ResolvedAttachRow.label`). Ruled 2026-08-25, decision 2: a
   *  search-wide comment is block framing rendered ONCE, never stamped onto
   *  each contributed card. */
  readonly searchLabel: string | null;
  /** The rows this link contributes, in order. Empty means the link named
   *  something with no results behind it — which resolves to nothing, and
   *  under decision 8 lands as a plain link. */
  readonly rows: readonly ResolvedAttachRow[];
}

/** Resolution, as the write seam performs it. Null = not in the writer's live
 *  store — which is no longer a refusal (decision 8): the link lands plain. */
export type AttachLinkResolver = (input: {
  item: AttachLinkItem;
}) => ResolvedAttachLink | null;

/** One pillar block, resolved: everything the renderer needs. A block names
 *  its PILLAR, never a search — several searches of one pillar merge into it
 *  (ruled 2026-08-25) — so what it carries of them is their capture WINDOW
 *  and their merged rows. */
export interface AttachPlan {
  readonly pillar: PillarCode;
  /** The contributing searches' capture instants, earliest and latest (U16).
   *  A single-search block carries the same instant on both ends and its
   *  caption prints the single-instant form, byte-identical to before. */
  readonly capturedAtRange: CapturedAtRange;
  readonly title: string | null;
  /** Whole-search links' labels, authored order, exact duplicates once — the
   *  block-level framing half of decision 2 (2026-08-25). They join the
   *  block's `about:` line beside the title (`attachBlockAbout`), rendered
   *  once, never per card. */
  readonly searchLabels: readonly string[];
  readonly rows: readonly ResolvedAttachRow[];
}

/** One link of a plain-links block, with whatever it DID resolve to.
 *
 *  `rows` is non-empty only in a mixed block — a link that resolved beside one
 *  that did not, where the whole block degrades to the plain kind (D28: to the
 *  whole, never to a part) but the resolved link's rows still render as its
 *  items, each under its DURABLE address. A link that resolved to nothing
 *  carries no rows and prints exactly as pasted, unaugmented (decision 8). */
export interface PlainAttachLink {
  readonly label: string | null;
  readonly url: string;
  readonly rows: readonly ResolvedAttachRow[];
}

/** A plain-links block: the decision-8 landing for a block with any link the
 *  writer's live store does not answer. No search, no pillar — the block's
 *  `kind:` prints the plain-link family and its capture time is the write's. */
export interface PlainLinksPlan {
  readonly title: string | null;
  /** Whole-search labels of the links that DID resolve rows into this mixed
   *  block — the same block framing `AttachPlan.searchLabels` carries, under
   *  the same ruling. A link that resolved nothing keeps its label on the
   *  pasted item itself (it is that one item's name), so it never enters
   *  here. */
  readonly searchLabels: readonly string[];
  readonly links: readonly PlainAttachLink[];
}

/** How one authored block materialises. A closed set — the write seam branches
 *  on it and nothing else does. */
export enum AttachPlanOutcome {
  /** Every link resolved: one block per pillar the links span, ordered by
   *  first pillar appearance (ruled 2026-08-25 — never a refusal, however
   *  many searches the links name). */
  Pillar = 'pillar',
  /** At least one link resolved to nothing and none of those is an action
   *  token: the plain-links block (decision 8). Never a refusal. */
  PlainLinks = 'plain_links',
  /** An action token in the block resolves to nothing. The block is handed
   *  back unmaterialised so the research-address guard — the ruled leak-guard
   *  copy's one home — refuses the write (U9). */
  ActionTokenUnresolvable = 'action_token_unresolvable',
}

export type AttachPlanResult =
  | { readonly outcome: AttachPlanOutcome.Pillar; readonly plans: readonly AttachPlan[] }
  | { readonly outcome: AttachPlanOutcome.PlainLinks; readonly plan: PlainLinksPlan }
  | {
      readonly outcome: AttachPlanOutcome.ActionTokenUnresolvable;
      /** The dead action addresses, verbatim as the writer wrote them. */
      readonly addresses: readonly string[];
    };

/** Resolve one block's links and decide what it materialises as. */
export function planAttachedResult(input: {
  parsed: ParsedAttachBlock;
  resolve: AttachLinkResolver;
}): AttachPlanResult {
  const resolved = input.parsed.links.map((item) => ({
    item,
    resolution: input.resolve({ item }),
  }));

  // A link that resolves to no row lands with the ones that resolve to
  // nothing at all: both name something no live record answers for, and both
  // are the plain-link landing under decision 8.
  const unresolved = resolved.filter(
    ({ resolution }) => resolution === null || resolution.rows.length === 0,
  );

  if (unresolved.length > 0) {
    // The guard's own reading decides "action token", so the block this seam
    // leaves unmaterialised is exactly the block the guard will refuse — one
    // predicate, two seams, no drift. The LINK-POSITION reading covers every
    // dress a trimmed action link wears (full URL, scheme-less, rootless
    // `/r/<token>`, the naked token itself); a foreign `/r/` path (a
    // subreddit, a docs page) carries no grammar token and is an ordinary URL
    // here too.
    const deadActionAddresses = unresolved
      .filter(({ item }) => offeredLinkActionAddress({ url: item.url }) !== null)
      .map(({ item }) => item.url);
    if (deadActionAddresses.length > 0) {
      return {
        outcome: AttachPlanOutcome.ActionTokenUnresolvable,
        addresses: deadActionAddresses,
      };
    }
    // Decision 8: the whole block materialises as the plain-link kind. The
    // degrade is to the WHOLE (D28) — a mixed block does not ship half a
    // pillar card — but a link that did resolve keeps its rows, so its items
    // still print under durable addresses instead of losing the result.
    return {
      outcome: AttachPlanOutcome.PlainLinks,
      plan: {
        title: input.parsed.title,
        searchLabels: searchLabelsOf({
          resolutions: resolved.flatMap(({ resolution }) =>
            resolution === null || resolution.rows.length === 0 ? [] : [resolution],
          ),
        }),
        links: resolved.map(({ item, resolution }) => ({
          label: item.label,
          url: item.url,
          rows: resolution === null ? [] : resolution.rows,
        })),
      },
    };
  }

  const resolutions = resolved
    .map(({ resolution }) => resolution)
    .filter((resolution): resolution is ResolvedAttachLink => resolution !== null);
  if (resolutions.length === 0) {
    // A parsed block always offers at least one link (`parseAttachBlock`
    // answers null otherwise), so an empty resolution set with nothing
    // unresolved cannot occur; the empty plain block keeps the reading total.
    return {
      outcome: AttachPlanOutcome.PlainLinks,
      plan: { title: input.parsed.title, searchLabels: [], links: [] },
    };
  }

  return {
    outcome: AttachPlanOutcome.Pillar,
    plans: pillarPlansOf({ title: input.parsed.title, resolutions }),
  };
}

// ─── the pillar partition (ruled 2026-08-25) ────────────────────────────────

/** One pillar's contributing resolutions, in authored-link order. */
interface PillarGroup {
  readonly pillar: PillarCode;
  readonly resolutions: ResolvedAttachLink[];
}

/** One plan per pillar the resolved links span, ordered by the pillar's first
 *  appearance among the authored links. The authored title copies onto EVERY
 *  resulting block (decision 2): the writer framed the whole attach, and each
 *  block is a piece of it. */
function pillarPlansOf(input: {
  title: string | null;
  resolutions: readonly ResolvedAttachLink[];
}): AttachPlan[] {
  const groups: PillarGroup[] = [];
  for (const resolution of input.resolutions) {
    const group = groups.find(
      (candidate) => candidate.pillar === resolution.search.pillar,
    );
    if (group === undefined) {
      groups.push({ pillar: resolution.search.pillar, resolutions: [resolution] });
    } else {
      group.resolutions.push(resolution);
    }
  }
  return groups.map((group) => ({
    pillar: group.pillar,
    capturedAtRange: capturedAtRangeOf({ resolutions: group.resolutions }),
    title: input.title,
    searchLabels: searchLabelsOf({ resolutions: group.resolutions }),
    rows: mergedRowsOf({ resolutions: group.resolutions }),
  }));
}

/** The group's whole-search labels: authored-link order, exact duplicates
 *  once. Framing renders once (decision 2), so two links labelled identically
 *  say one thing. */
function searchLabelsOf(input: {
  resolutions: readonly ResolvedAttachLink[];
}): string[] {
  return [
    ...new Set(
      input.resolutions.flatMap((resolution) =>
        resolution.searchLabel === null ? [] : [resolution.searchLabel],
      ),
    ),
  ];
}

// ─── the block's framing line (decision 2, ruled 2026-08-25) ────────────────

/** What joins the writer's title and the whole-search labels on one `about:`
 *  line. The same reader-facing joiner the about seeds already use. */
export const ATTACH_ABOUT_FRAMING_SEPARATOR = ' · ';

/** What one composed `about:` value may carry: the persisted grammar's line
 *  ceiling less the directive's own `about: ` head.
 *
 *  It exists because the framing line is now a COMPOSITION. A title and a
 *  label are each bounded at 300 (`ATTACH_MAX_TITLE_LENGTH`,
 *  `ATTACH_MAX_LABEL_LENGTH`), and two legal values plus the separator clear
 *  600 — at which point the renderer drops the directive line WHOLE
 *  (`directiveLinesOf`) and the writer's framing vanishes with nothing said.
 *  Bounding the composition here keeps the same posture the two inputs
 *  already have: truncate at the seam that composes, never drop in silence. */
export const ATTACH_MAX_ABOUT_LENGTH =
  ATTACH_PERSISTED_MAX_LINE_LENGTH -
  (AttachDirectiveKey.About.length + ATTACH_FIELD_SEPARATOR.length);

/** The block's `about:` value: the writer's title, then each whole-search
 *  label, one line, rendered once — the ruled home of a search-wide comment.
 *  Null when the writer framed nothing, exactly as before: a block written
 *  without framing carries no `about` line rather than a made-up one.
 *
 *  Truncated to `ATTACH_MAX_ABOUT_LENGTH` so the line it becomes provably
 *  survives the renderer's ceiling. The title leads, so the writer's own
 *  framing of the attach is the part that always fits and the search labels
 *  degrade from the tail. */
export function attachBlockAbout(input: {
  title: string | null;
  searchLabels: readonly string[];
}): string | null {
  const parts = [
    ...(input.title === null || input.title.length === 0 ? [] : [input.title]),
    ...input.searchLabels.filter((label) => label.length > 0),
  ];
  if (parts.length === 0) return null;
  const composed = parts.join(ATTACH_ABOUT_FRAMING_SEPARATOR);
  return composed.length > ATTACH_MAX_ABOUT_LENGTH
    ? composed.slice(0, ATTACH_MAX_ABOUT_LENGTH).trimEnd()
    : composed;
}

/** The contributing searches' capture window — earliest and latest instant
 *  among the group's resolutions. An instant that does not parse cannot bound
 *  a window; if none parses the first resolution's value rides through on both
 *  ends, and the renderer refuses to caption it exactly as it refuses a single
 *  unreadable instant today. */
function capturedAtRangeOf(input: {
  resolutions: readonly ResolvedAttachLink[];
}): CapturedAtRange {
  const dated = input.resolutions
    .map((resolution) => ({
      instant: resolution.capturedAt,
      time: new Date(resolution.capturedAt).getTime(),
    }))
    .filter((entry) => !Number.isNaN(entry.time));
  const fallback = input.resolutions[0]?.capturedAt ?? '';
  if (dated.length === 0) return { min: fallback, max: fallback };
  const min = dated.reduce((left, right) => (right.time < left.time ? right : left));
  const max = dated.reduce((left, right) => (right.time > left.time ? right : left));
  return { min: min.instant, max: max.instant };
}

// ─── the merge (decision 5, ratified 2026-08-25) ────────────────────────────

/** One row as one search contributed it, dated by that search's own capture —
 *  the "newer capture" the duplicate preference compares. */
interface RowOccurrence {
  readonly row: ResolvedAttachRow;
  readonly capturedAt: string;
}

/** One ITEM, as the merge knows it: every occurrence proven to be it, and the
 *  one occurrence the block renders for it.
 *
 *  Every occurrence is remembered because identity EVIDENCE is per occurrence.
 *  A row carrying both `isbn` and `olKey` is the same item as one carrying only
 *  `isbn` and as one carrying only `olKey`, while those two prove nothing about
 *  each other; a cluster that remembered only its preferred row would lose
 *  whichever bridge that row does not carry, and one item would render twice on
 *  nothing but the order the writer happened to paste its links in. */
interface ItemCluster {
  readonly occurrences: RowOccurrence[];
  preferred: RowOccurrence;
}

/**
 * The union of the group's rows: authored-link order, then row order, each
 * item once.
 *
 * Two dedupes, one pass. A row named twice by REF — the writer naming a row
 * and then its search — is the same record occurrence and is simply not taken
 * again. Across searches the ruled item identity decides (`sameAttachedItem`),
 * and a recognised duplicate resolves by preference: the detailed occurrence,
 * else the newer capture, kept in the FIRST occurrence's slot with ONLY its
 * own addresses and label — a discarded occurrence's label dies with it.
 */
function mergedRowsOf(input: {
  resolutions: readonly ResolvedAttachLink[];
}): ResolvedAttachRow[] {
  const clusters: ItemCluster[] = [];
  const seenRefs = new Set<string>();
  for (const resolution of input.resolutions) {
    for (const row of resolution.rows) {
      if (seenRefs.has(row.record.ref)) continue;
      seenRefs.add(row.record.ref);
      absorbOccurrence({
        clusters,
        occurrence: { row, capturedAt: resolution.capturedAt },
      });
    }
  }
  return clusters.map((cluster) => cluster.preferred.row);
}

/**
 * One occurrence into the item it belongs to.
 *
 * It joins the EARLIEST cluster that recognises it — the ruled first-occurrence
 * slot — and any later cluster it also recognises folds into that one: a row
 * that is the same item as both proves those two are each other, and how many
 * proven duplicates survive cannot depend on the order the writer pasted the
 * links.
 *
 * A join or a fold that would put two rows of ONE search into one item is
 * refused. That search's own list is its own truth (the cross-search rule of
 * `sameAttachedItem`), and a bridge through another search does not rewrite it.
 */
function absorbOccurrence(input: {
  clusters: ItemCluster[];
  occurrence: RowOccurrence;
}): void {
  const matched = input.clusters.filter(
    (cluster) =>
      clusterRecognises({ cluster, row: input.occurrence.row }) &&
      clusterAdmits({ cluster, searchSeq: input.occurrence.row.record.searchSeq }),
  );
  const target = matched[0];
  if (target === undefined) {
    input.clusters.push({
      occurrences: [input.occurrence],
      preferred: input.occurrence,
    });
    return;
  }
  for (const bridged of matched.slice(1)) {
    if (!clustersFoldable({ target, bridged })) continue;
    target.occurrences.push(...bridged.occurrences);
    target.preferred = preferredOccurrenceOf({
      incumbent: target.preferred,
      challenger: bridged.preferred,
    });
    input.clusters.splice(input.clusters.indexOf(bridged), 1);
  }
  target.occurrences.push(input.occurrence);
  target.preferred = preferredOccurrenceOf({
    incumbent: target.preferred,
    challenger: input.occurrence,
  });
}

/** Whether a cluster already holds an occurrence this row is the same item as
 *  — ANY of them, because any one of them proves it. */
function clusterRecognises(input: {
  cluster: ItemCluster;
  row: ResolvedAttachRow;
}): boolean {
  return input.cluster.occurrences.some((occurrence) =>
    sameAttachedItem({ left: occurrence.row, right: input.row }),
  );
}

/** Whether a cluster can take a row of this search without collapsing two rows
 *  of ONE search into one item. */
function clusterAdmits(input: { cluster: ItemCluster; searchSeq: number }): boolean {
  return input.cluster.occurrences.every(
    (occurrence) => occurrence.row.record.searchSeq !== input.searchSeq,
  );
}

/** Whether two clusters a bridging row recognises may become one: only when no
 *  search contributed to both. */
function clustersFoldable(input: { target: ItemCluster; bridged: ItemCluster }): boolean {
  return input.bridged.occurrences.every((occurrence) =>
    clusterAdmits({
      cluster: input.target,
      searchSeq: occurrence.row.record.searchSeq,
    }),
  );
}

/** The ruled item identity: same row kind, then native identifiers where both
 *  rows offer a shared key, else the normalized destination. A pair neither
 *  can judge is not the same item — no identity, no merge. (The pillar half of
 *  the identity is the partition itself: rows meet here per pillar group.)
 *
 *  CROSS-SEARCH ONLY: two distinct rows of ONE search are two items that
 *  search itself served — its own list is its own truth, and the merge exists
 *  for the same item RE-FOUND by another search. (One row attached twice is
 *  the ref dedupe above, not this.) */
function sameAttachedItem(input: {
  left: ResolvedAttachRow;
  right: ResolvedAttachRow;
}): boolean {
  if (input.left.record.searchSeq === input.right.record.searchSeq) return false;
  if (input.left.record.kind !== input.right.record.kind) return false;
  const byNativeIds = sharedNativeIdentity({
    left: input.left.record,
    right: input.right.record,
  });
  if (byNativeIds !== null) return byNativeIds;
  const left = normalizedDestinationOf({ url: input.left.record.address.url });
  const right = normalizedDestinationOf({ url: input.right.record.address.url });
  return left !== null && left === right;
}

/** Native-identifier identity, where it can judge at all: equal values on
 *  EVERY key both rows carry, at least one key shared. A shared key that
 *  disagrees is a decisive mismatch — two videos differing only by `videoId`
 *  must never fall through to a forgiving URL read. Null when the rows share
 *  no key (or either carries none): inapplicable, and the destination
 *  identity answers instead. */
function sharedNativeIdentity(input: {
  left: RowRefRecord;
  right: RowRefRecord;
}): boolean | null {
  const left = input.left.address.nativeIds;
  const right = input.right.address.nativeIds;
  if (left === undefined || right === undefined) return null;
  const sharedKeys = Object.keys(left).filter((key) => right[key] !== undefined);
  if (sharedKeys.length === 0) return null;
  return sharedKeys.every((key) => left[key] === right[key]);
}

/** What a captured value can drag onto a URL's tail: sentence punctuation, path
 *  slashes, and any mix of the two — `…/item./` is the same destination as
 *  `…/item`, so the run is stripped WHOLE rather than one class after the
 *  other, which would leave whichever class the other one shielded. Stripped
 *  before two destinations are compared, never from anything that is printed. */
const URL_TAIL_NOISE_REGEX = /[.,;:!?/]+$/;

/** The row's captured destination, normalized for IDENTITY ONLY: query and
 *  fragment dropped, tail punctuation and trailing slashes stripped. (Scheme
 *  and host arrive lowercased from the URL parser; the path keeps its case —
 *  paths are case-significant and lowering them would merge distinct
 *  destinations.) Null when the row carries no parseable destination, which
 *  is "no identity" — never a wildcard. */
function normalizedDestinationOf(input: { url: string | undefined }): string | null {
  if (input.url === undefined) return null;
  let parsed: URL;
  try {
    parsed = new URL(input.url);
  } catch {
    return null;
  }
  const path = parsed.pathname.replace(URL_TAIL_NOISE_REGEX, '');
  return `${parsed.protocol}//${parsed.host}${path}`;
}

/** Which of two occurrences of one item the block keeps: the detailed one —
 *  the ruled "its more detailed version" — else the newer capture; a tie (or
 *  an unreadable instant) keeps the incumbent, whose slot the winner occupies
 *  either way. */
function preferredOccurrenceOf(input: {
  incumbent: RowOccurrence;
  challenger: RowOccurrence;
}): RowOccurrence {
  const incumbentDetailed = input.incumbent.row.detailPayload !== null;
  const challengerDetailed = input.challenger.row.detailPayload !== null;
  if (incumbentDetailed !== challengerDetailed) {
    return incumbentDetailed ? input.incumbent : input.challenger;
  }
  const incumbentTime = new Date(input.incumbent.capturedAt).getTime();
  const challengerTime = new Date(input.challenger.capturedAt).getTime();
  return !Number.isNaN(incumbentTime) &&
    !Number.isNaN(challengerTime) &&
    challengerTime > incumbentTime
    ? input.challenger
    : input.incumbent;
}
